import * as React from 'react';

export interface TimeConfig {
  endTime?: number;
  startTime?: number;
  interval?: number;
  step?: number;
  type?: 'INCREMENT' | 'DECREMENT';
}

const initialConfig = {
  endTime: 0,
  startTime: 10,
  interval: 1000,
  step: 1,
  type: 'DECREMENT'
};

/**
 * useTimer is a hook that creates a timer for a component
 * @param {*} onTimeout - Callback Function to fire when timeout
 * @param {*} config - Configurations to set for controlling the timer
 */

export function useTimer(onTimeout: () => void, config?: TimeConfig) {
  const { endTime, startTime, interval, step, type } = { ...initialConfig, ...config };

  const [time, setTime] = React.useState<number>(startTime);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  // on mount start call counter, upon unmount clear interval
  React.useEffect(() => {
    counter();
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // upon time change call reset
  React.useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [time]);

  // reset will call onTimeout callback function and clear interval when time has hit the end time
  const reset = () => {
    if (time === endTime) {
      onTimeout();
      clearInterval(intervalRef.current);
    }
  };

  // counter will set interval with the configs set
  const counter = () => {
    intervalRef.current = setInterval(() => {
      setTime((prevTime) => (type === 'DECREMENT' ? prevTime - step : prevTime + step));
    }, interval);
  };

  // returns the time
  return time;
}
